// 계약 설정 한 벌 — 화면과 서버 액션이 같이 쓰는 단위 변환·검증·저장 직렬화.

import { z } from "zod";

import { subjectJosa, topicJosa } from "../../lib/josa";
import { formatNumber } from "../../lib/format";
import { parseOptionalNameText, type NameParsed } from "../../lib/name-text";
import { PERSON_NAME_MAX } from "../onboarding/rules";
import {
  BP_SCALE,
  assertPaymentRatios,
  assertSpecialTerms,
  assertWarrantyAgainstStandard,
  assertWarrantyTerms,
  dailyDelayPenaltyOf,
  delayPenaltyOf,
  lateInterestOf,
  penaltyOf,
  usesDelayPenalty,
} from "./calc";
import {
  SettingReader,
  contractorFrom,
  parseContractSettings,
} from "./build-from-quote";
import { defaultClauses, judgeClauseSource } from "./clauses";
import { DEFAULT_CLAUSE_SOURCE } from "./defaults";
import {
  ClauseSource,
  ContractClause,
  PaymentPlanRow,
  SpecialTerm,
  WarrantyTerm,
} from "./types";
import type { OrgSettingKey } from "../org/types";

export type ClauseBase = Exclude<ClauseSource, "CUSTOM">;
export const CLAUSE_BASES: readonly ClauseBase[] = ["FTC_10079", "MOLIT"];

export const isClauseBase = (value: unknown): value is ClauseBase =>
  typeof value === "string" && (CLAUSE_BASES as readonly string[]).includes(value);

/* ── 단위 ─────────────────────────────────────────────────────── */

export type RateUnit = "percent" | "permille";

const BP_PER_UNIT: Record<RateUnit, number> = { percent: 100, permille: 10 };

export const RATE_UNIT_SUFFIX: Record<RateUnit, string> = { percent: "%", permille: "‰" };

// bp → 화면 값. 1bp 는 0.01% · 0.1‰ 이라 이 자리수까지 정확히 떨어진다.
export const bpToRate = (bp: number, unit: RateUnit): number => bp / BP_PER_UNIT[unit];

export type RateParse = { ok: true; bp: number } | { ok: false; message: string };

// 십진 소수만 — Number() 가 받는 16진·지수·빈 문자열을 막는다.
const DECIMAL = /^(?:\d+(?:\.\d*)?|\.\d+)$/;

// 화면 값 → bp. 1bp 보다 잘게 적으면 반올림하지 않고 막는다(계약서 수가 조용히 바뀐다).
export const rateToBp = (raw: string, unit: RateUnit): RateParse => {
  const text = raw.trim();
  if (text === "") return { ok: false, message: "값을 입력하세요" };
  if (/^-\s*\d/.test(text)) return { ok: false, message: "0 이상이어야 합니다" };
  if (!DECIMAL.test(text)) return { ok: false, message: "숫자를 입력하세요" };
  const value = Number(text);
  const scaled = value * BP_PER_UNIT[unit];
  const bp = Math.round(scaled);
  if (Math.abs(scaled - bp) > 1e-9 * Math.max(1, Math.abs(scaled))) {
    return {
      ok: false,
      message: `소수 ${unit === "percent" ? "둘째" : "첫째"} 자리까지 적을 수 있습니다`,
    };
  }
  return { ok: true, bp };
};

export type RateKey =
  | "delay_rate_bp"
  | "delay_cap_bp"
  | "late_interest_rate_bp"
  | "penalty_rate_bp"
  | "warranty_deposit_bp";

export interface RateField {
  key: RateKey;
  label: string;
  unit: RateUnit;
  hint: string;
  // 공정위 서식에서만 붙이는 안내 — 그 서식에서만 검사하는 규칙이다.
  ftcHint?: string;
  // 지체상금 서식에서만 쓰는 칸(usesDelayPenalty).
  delayOnly: boolean;
}

export const RATE_FIELDS: readonly RateField[] = [
  {
    key: "delay_rate_bp",
    label: "지체상금률",
    unit: "permille",
    hint: "1일당 계약금액의 천분율. 공공공사 기준 1일 1천분의 0.5 (국가계약법 시행규칙 제75조)",
    delayOnly: true,
  },
  {
    key: "delay_cap_bp",
    label: "지체상금 한도",
    unit: "percent",
    hint: "계약금액 대비. 원문에 없는 수라 계약서에 실으려면 특약으로 적습니다",
    delayOnly: true,
  },
  {
    key: "late_interest_rate_bp",
    label: "지연이자율 (연)",
    unit: "percent",
    hint: "대금·완공이 늦을 때의 연체이율. 0 은 정하지 않음",
    delayOnly: false,
  },
  {
    key: "penalty_rate_bp",
    label: "착수 전 해제 위약금률",
    unit: "percent",
    hint: "총 공사금액 대비",
    ftcHint: "공정위 서식은 10% 를 넘을 수 없습니다",
    delayOnly: false,
  },
  {
    key: "warranty_deposit_bp",
    label: "하자보수보증금률",
    unit: "percent",
    hint: "계약금액 대비",
    delayOnly: false,
  },
];

// 안내 문구는 검사(penaltyOf·assertWarrantyAgainstStandard)와 같은 판정 서식을 따른다.
export const rateHintOf = (field: RateField, source: ClauseSource): string =>
  source === "FTC_10079" && field.ftcHint ? `${field.hint}. ${field.ftcHint}` : field.hint;

export const warrantyHintOf = (source: ClauseSource): string =>
  source === "FTC_10079"
    ? "일반 기간과 공종별 예외를 적습니다. 공정위 서식은 창호 2년·유리 1년을 따로 둡니다."
    : "일반 기간과 공종별 예외를 적습니다.";

/* ── 설정 한 벌 ───────────────────────────────────────────────── */

export interface ContractSettingsForm {
  clause_base: ClauseBase;
  clauses: ContractClause[];
  special_terms: SpecialTerm[];
  payments: PaymentPlanRow[];
  delay_rate_bp: number;
  delay_cap_bp: number;
  late_interest_rate_bp: number;
  penalty_rate_bp: number;
  warranty_deposit_bp: number;
  warranty_months: number;
  warranty_terms: WarrantyTerm[];
  company_ceo: string;
  company_biz_no: string;
  company_const_license_no: string;
}

// 어느 칸인지 — `payments.1.trigger_text` 처럼 첫 마디가 화면 구획이다.
export interface SettingsIssue {
  field: string;
  message: string;
}

// 특약·회차 번호는 자리 순서로 다시 매긴다.
const renumberTerms = (terms: readonly SpecialTerm[]): SpecialTerm[] =>
  terms.map((t, i) => ({ no: i + 1, title: t.title.trim(), body: t.body.trim() }));

const guard = (issues: SettingsIssue[], field: string, fn: () => void): boolean => {
  try {
    fn();
    return true;
  } catch (error) {
    issues.push({
      field,
      message: error instanceof Error ? error.message : "값을 확인해 주세요",
    });
    return false;
  }
};

// 지급회차만 따로 — 화면이 합계 줄에서 실시간으로 쓴다.
export const paymentPlanIssues = (rows: readonly PaymentPlanRow[]): SettingsIssue[] => {
  const issues: SettingsIssue[] = [];
  rows.forEach((row, i) => {
    if (row.label.trim() === "") {
      issues.push({ field: `payments.${i}.label`, message: `${i + 1}회차 이름이 비어 있습니다` });
    }
    if (row.trigger_text.trim() === "") {
      issues.push({
        field: `payments.${i}.trigger_text`,
        message: `${i + 1}회차의 지급 시기가 비어 있습니다`,
      });
    }
  });
  guard(issues, "payments.sum", () => assertPaymentRatios(rows.map((r) => r.ratio_bp)));
  return issues;
};

// 조 번호 표기 — 국토부는 가지번호(22조의2)가 있어 label 을 먼저 쓴다.
export const clauseHeading = (clause: Pick<ContractClause, "no" | "label">): string => {
  if (!clause.label) return `제${clause.no}조`;
  return clause.label.startsWith("제") ? clause.label : `제${clause.label}`;
};

const BIZ_NO = /^\d{3}-?\d{2}-?\d{5}$/;

// 대표자 — 비우거나 보이지 않는 글자뿐이면 빈 값(선택 칸) · 적었으면 시공사 만들기와 같은 이름 검사(코드포인트 40 · 저장값은 정리한 값).
export const parseContractCeo = (raw: string): NameParsed =>
  parseOptionalNameText(raw, { label: "대표자 이름", max: PERSON_NAME_MAX, url: true });

// 저장하는 대표자 값 — 저장(contractSettingValues)과 저장 뒤 화면 칸·요약이 같이 쓴다(검사를 지난 뒤라 실패 분기는 원문 다듬기).
export const savedContractCeo = (raw: string): string => {
  const ceo = parseContractCeo(raw);
  return ceo.ok ? ceo.value : raw.trim();
};

// 모양이 맞은 뒤의 규칙. 계약 계산(calc)의 검사를 그대로 불러 같은 기준으로 막는다.
export const validateContractSettings = (form: ContractSettingsForm): SettingsIssue[] => {
  const issues: SettingsIssue[] = [];
  const source = judgeClauseSource(form.clause_base, form.clauses);

  form.clauses.forEach((clause, i) => {
    if (clause.title.trim() === "" || clause.body.trim() === "") {
      issues.push({
        field: `clauses.${i}`,
        message: `${clauseHeading(clause)}의 제목이나 본문이 비어 있습니다`,
      });
    }
  });

  guard(issues, "special_terms", () => assertSpecialTerms(renumberTerms(form.special_terms)));
  issues.push(...paymentPlanIssues(form.payments));

  guard(issues, "rates.delay_rate_bp", () => dailyDelayPenaltyOf(0, form.delay_rate_bp));
  guard(issues, "rates.delay_cap_bp", () =>
    delayPenaltyOf({ amount: 0, days: 0, rate_bp: 0, cap_bp: form.delay_cap_bp })
  );
  guard(issues, "rates.late_interest_rate_bp", () =>
    lateInterestOf({ base_amount: 0, days: 0, rate_bp: form.late_interest_rate_bp })
  );
  guard(issues, "rates.penalty_rate_bp", () => penaltyOf(0, form.penalty_rate_bp, source));

  if (guard(issues, "warranty.terms", () => assertWarrantyTerms(form.warranty_terms))) {
    guard(issues, "warranty.months", () =>
      assertWarrantyAgainstStandard(source, form.warranty_months, form.warranty_terms)
    );
  }

  const ceo = parseContractCeo(form.company_ceo);
  if (!ceo.ok) issues.push({ field: "company.company_ceo", message: ceo.error });

  const bizNo = form.company_biz_no.trim();
  if (bizNo !== "" && !BIZ_NO.test(bizNo)) {
    issues.push({
      field: "company.company_biz_no",
      message: "사업자등록번호는 000-00-00000 모양의 숫자 10자리입니다",
    });
  }
  return issues;
};

const bp = (label: string) =>
  z
    .number({ message: `${label}${topicJosa(label)} 숫자여야 합니다` })
    .int({ message: `${label}${topicJosa(label)} 정수(bp)여야 합니다` })
    .min(0, { message: `${label}${topicJosa(label)} 0 이상이어야 합니다` })
    .max(BP_SCALE, { message: `${label}${topicJosa(label)} 100% 를 넘을 수 없습니다` });

const shortText = (label: string, max: number) =>
  z.string({ message: `${label}${subjectJosa(label)} 없습니다` }).max(max, {
    message: `${label}${topicJosa(label)} ${formatNumber(max)}자를 넘을 수 없습니다`,
  });

// 저장 스키마. 모양·범위를 보고, 규칙은 validateContractSettings 가 칸마다 짚는다.
export const contractSettingsSchema = z
  .object({
    clause_base: z.enum(["FTC_10079", "MOLIT"], { message: "서식을 고르세요" }),
    clauses: z
      .array(
        z.object({
          no: z.number().int().min(1),
          label: z
            .string()
            .max(20)
            .nullish()
            .transform((v) => v ?? undefined),
          // 원문 대조는 한 글자 단위라 trim 하지 않는다.
          title: shortText("조항 제목", 200),
          body: shortText("조항 본문", 20_000),
        })
      )
      .min(1, { message: "조항이 하나는 있어야 합니다" })
      .max(100, { message: "조항은 100개까지입니다" }),
    special_terms: z
      .array(
        z.object({
          no: z.number().int(),
          title: shortText("특약 제목", 200),
          body: shortText("특약 내용", 5_000),
        })
      )
      .max(50, { message: "특약은 50개까지입니다" }),
    payments: z
      .array(
        z.object({
          seq: z.number().int(),
          label: shortText("회차 이름", 40),
          ratio_bp: z.number({ message: "지급 비율은 숫자여야 합니다" }),
          trigger_text: shortText("지급 시기", 200),
        })
      )
      .max(20, { message: "지급회차는 20회까지입니다" }),
    delay_rate_bp: bp("지체상금률"),
    delay_cap_bp: bp("지체상금 한도"),
    late_interest_rate_bp: bp("지연이자율"),
    penalty_rate_bp: bp("위약금률"),
    warranty_deposit_bp: bp("하자보수보증금률"),
    warranty_months: z
      .number({ message: "하자담보 기간은 숫자여야 합니다" })
      .int({ message: "하자담보 기간은 개월 수(정수)입니다" })
      .min(0, { message: "하자담보 기간은 0 이상이어야 합니다" })
      .max(600, { message: "하자담보 기간은 600개월을 넘을 수 없습니다" }),
    warranty_terms: z
      .array(z.object({ label: shortText("공종 이름", 60), months: z.number() }))
      .max(50, { message: "하자담보 예외는 50줄까지입니다" }),
    company_ceo: z.string({ message: "대표자가 없습니다" }),
    company_biz_no: shortText("사업자등록번호", 20),
    company_const_license_no: shortText("건설업 등록번호", 60),
  })
  .superRefine((form, ctx) => {
    for (const issue of validateContractSettings(form)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: issue.message,
        path: issue.field.split("."),
      });
    }
  });

// zod 경로를 화면 구획 이름으로. 모양 오류(`delay_rate_bp`)도 구획(`rates.`) 아래로 모은다.
export const issuesOf = (error: z.ZodError): SettingsIssue[] =>
  error.issues.map((issue) => {
    const path = issue.path.join(".");
    // 화면의 비율 칸은 % 글자(`ratio`)라 bp 경로를 그 이름으로 돌린다.
    const ratio = /^payments\.(\d+)\.ratio_bp$/.exec(path);
    if (ratio) return { field: `payments.${ratio[1]}.ratio`, message: issue.message };
    const rate = RATE_FIELDS.find((f) => f.key === path);
    if (rate) return { field: `rates.${path}`, message: issue.message };
    if (path === "warranty_months") return { field: "warranty.months", message: issue.message };
    if (path.startsWith("warranty_terms")) return { field: "warranty.terms", message: issue.message };
    if (path.startsWith("company_")) return { field: `company.${path}`, message: issue.message };
    return { field: path || "form", message: issue.message };
  });

// 저장할 키·값. 원문 그대로인 조항은 빈 배열로 남긴다 — 템플릿 판이 올라가면 따라간다.
export const contractSettingValues = (
  form: ContractSettingsForm
): [OrgSettingKey, string][] => {
  const source = judgeClauseSource(form.clause_base, form.clauses);
  return [
    ["contract.clause_base", form.clause_base],
    ["contract.clause_source", source],
    [
      "contract.clauses",
      source === "CUSTOM"
        ? JSON.stringify(
            form.clauses.map((c) => ({
              no: c.no,
              ...(c.label ? { label: c.label } : {}),
              title: c.title,
              body: c.body,
            }))
          )
        : "[]",
    ],
    ["contract.special_terms", JSON.stringify(renumberTerms(form.special_terms))],
    [
      "contract.payments",
      JSON.stringify(
        form.payments.map((p, i) => ({
          seq: i + 1,
          label: p.label.trim(),
          ratio_bp: p.ratio_bp,
          trigger_text: p.trigger_text.trim(),
        }))
      ),
    ],
    ["contract.delay_rate_bp", String(form.delay_rate_bp)],
    ["contract.delay_cap_bp", String(form.delay_cap_bp)],
    ["contract.late_interest_rate_bp", String(form.late_interest_rate_bp)],
    ["contract.penalty_rate_bp", String(form.penalty_rate_bp)],
    ["contract.warranty_deposit_bp", String(form.warranty_deposit_bp)],
    ["contract.warranty_months", String(form.warranty_months)],
    [
      "contract.warranty_terms",
      JSON.stringify(form.warranty_terms.map((t) => ({ label: t.label.trim(), months: t.months }))),
    ],
    ["company_ceo", savedContractCeo(form.company_ceo)],
    ["company_biz_no", form.company_biz_no.trim()],
    ["company_const_license_no", form.company_const_license_no.trim()],
  ];
};

// 저장된 설정 → 화면 한 벌. 계약 생성(parseContractSettings)과 같은 해석을 쓴다.
export const contractSettingsFormOf = (get: SettingReader): ContractSettingsForm => {
  const parsed = parseContractSettings(get);
  const savedBase = (get("contract.clause_base") ?? "").trim();
  const clause_base: ClauseBase = isClauseBase(savedBase)
    ? savedBase
    : isClauseBase(parsed.clause_source)
      ? parsed.clause_source
      : isClauseBase(DEFAULT_CLAUSE_SOURCE)
        ? DEFAULT_CLAUSE_SOURCE
        : "FTC_10079";
  const company = contractorFrom(get, { name: "", address: "", tel: "" });
  return {
    clause_base,
    clauses:
      parsed.clauses.length > 0
        ? parsed.clauses.map((c) => ({ ...c }))
        : defaultClauses(clause_base),
    special_terms: parsed.special_terms.map((t) => ({ ...t })),
    payments: parsed.payments.map((p, i) => ({
      seq: i + 1,
      label: p.label,
      ratio_bp: p.ratio_bp,
      trigger_text: p.trigger_text,
    })),
    delay_rate_bp: parsed.delay_rate_bp,
    delay_cap_bp: parsed.delay_cap_bp,
    late_interest_rate_bp: parsed.late_interest_rate_bp,
    penalty_rate_bp: parsed.penalty_rate_bp,
    warranty_deposit_bp: parsed.warranty_deposit_bp,
    warranty_months: parsed.warranty_months,
    warranty_terms: parsed.warranty_terms.map((t) => ({ ...t })),
    company_ceo: company.ceo,
    company_biz_no: company.biz_no,
    company_const_license_no: company.const_license_no,
  };
};

/* ── 특약 예시 ────────────────────────────────────────────────── */

export interface SpecialTermSuggestion {
  // 이 낱말이 이미 특약에 있으면 다시 권하지 않는다.
  covers: string;
  title: string;
  body: string;
}

const pct = (value: number): string => `${formatNumber(bpToRate(value, "percent"))}%`;

// 서식 원문이 담지 않는 것(tests/contract-settings.test.ts 의 목록)을 특약 예시로 권한다.
export const suggestSpecialTerms = (
  base: ClauseBase,
  current: Pick<ContractSettingsForm, "special_terms" | "delay_cap_bp">
): SpecialTermSuggestion[] => {
  const all: (SpecialTermSuggestion & { bases: readonly ClauseBase[] })[] = [
    {
      bases: ["FTC_10079"],
      covers: "인지세",
      title: "인지세",
      body: "이 계약서에 붙이는 인지세는 도급인과 수급인이 절반씩 부담한다.",
    },
    {
      bases: ["FTC_10079", "MOLIT"],
      covers: "관리사무소",
      title: "관리사무소 비용",
      body: "공사 기간 중 관리사무소가 요구하는 승강기 사용료·보양 비용 등 공사에 따른 비용은 수급인이 부담한다.",
    },
    {
      bases: ["FTC_10079"],
      covers: "공사보증금",
      title: "공사보증금",
      body: "도급인이 요청하면 수급인은 계약 이행을 보증하는 보증서(보증보험증권 등)를 제출하며, 발급 비용은 수급인이 부담한다.",
    },
    {
      bases: ["FTC_10079", "MOLIT"],
      covers: "이웃",
      title: "이웃 피해",
      body: "공사 중 소음·분진·누수 등으로 이웃에게 끼친 손해는 수급인이 책임지고 처리한다. 다만 도급인의 지시나 건물 자체의 하자로 생긴 손해는 그러하지 아니하다.",
    },
    {
      bases: ["MOLIT"],
      covers: "지체상금 한도",
      title: "지체상금 한도",
      body: `지체상금의 총액은 계약금액의 ${pct(current.delay_cap_bp)}를 넘지 않는다.`,
    },
  ];
  const written = current.special_terms.map((t) => `${t.title} ${t.body}`).join("\n");
  return all
    .filter((s) => s.bases.includes(base) && !written.includes(s.covers))
    .map(({ covers, title, body }) => ({ covers, title, body }));
};

// 이 서식에서 지체상금 칸을 쓰는가 — 화면 안내용으로 판정만 넘긴다.
export const showsDelayPenalty = (form: Pick<ContractSettingsForm, "clause_base" | "clauses">): boolean =>
  usesDelayPenalty(judgeClauseSource(form.clause_base, form.clauses));
